import { AgreeToTermsPolicy, experience, ExtraParamsKey, SignInMode } from '@logto/schemas';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import SingleSignOnFormModeContext from '@/Providers/SingleSignOnFormModeContextProvider/SingleSignOnFormModeContext';
import Divider from '@/components/Divider';
import GoogleOneTap from '@/components/GoogleOneTap';
import TextLink from '@/components/TextLink';
import SocialSignInList from '@/containers/SocialSignInList';
import { trackEvent } from '@/utils/analytics';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useLoginHint from '@/hooks/use-login-hint';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';
import { getRuntimeConfig } from '@/utils/config';
import { logtoCookies } from '@/utils/cookies';

import ErrorPage from '../ErrorPage';

import Main from './Main';
import styles from './index.module.scss';
import { Footer } from '@/components/Footer/Footer';
import LINE from '@/ui-assets/images/line.png';
import Google from '@/ui-assets/images/google.png';
import { SignInExperienceResponse } from '@/types';

/** getEnhancedSocialConnectors() fixes blurry icons on Safari Mobile */
function getEnhancedSocialConnectors(socialConnectors: SignInExperienceResponse['socialConnectors']) {
  return socialConnectors.map((social) => {
    if (social.target === 'line') {
      return ({
        ...social,
        logo: LINE,
        logoDark: LINE,
      });
    }

    if (social.target === 'google') {
      return ({
        ...social,
        logo: Google,
        logoDark: Google,
      });
    }

    return social;
  });
}

const SignInFooters = () => {
  const { t } = useTranslation();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { signInMethods, signUpMethods, socialConnectors, signInMode, singleSignOnEnabled } =
    useSieMethods();

  const enhancedSocialConnectors = getEnhancedSocialConnectors(socialConnectors);

  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const handleSsoNavigation = useCallback(async () => {
    /**
     * Check if the user has agreed to the terms and privacy policy before navigating to the SSO page
     * when the policy is set to `Manual`
     */
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }
    navigate('/single-sign-on/email');
  }, [agreeToTermsPolicy, navigate, termsValidation]);

  /* Hide footers when showing Single Sign On form */
  if (showSingleSignOnForm) {
    return null;
  }

  return (
    <>
      {
        // Single Sign On footer
        singleSignOnEnabled && (
          <>
            <div className={styles.singleSignOn}>
              {t('description.use')}{' '}
              <TextLink
                text="action.single_sign_on"
                onClick={() => {
                  trackEvent(
                    'single_sign_on_link',
                    'clicked',
                    { eventId: 'sign_in_single_sign_on_link_clicked' },
                    {}
                  );
                  void handleSsoNavigation();
                }}
              />
            </div>
            {
              /**
               * If only SSO sign-in methods are available, display the agreement checkbox when the agreement policy is `Manual`.
               */
              signInMethods.length === 0 &&
              enhancedSocialConnectors.length === 0 &&
              agreeToTermsPolicy === AgreeToTermsPolicy.Manual && (
                <TermsAndPrivacyCheckbox className={styles.checkboxForSsoOnly} />
              )
            }
          </>
        )
      }
      {
        // Create Account footer
        signInMode === SignInMode.SignInAndRegister && signUpMethods.length > 0 && false && (
          <div className={styles.createAccount}>
            {t('description.no_account')}{' '}
            <TextLink
              replace
              to="/register"
              text="action.create_account"
              onClick={() => {
                trackEvent(
                  'create_account_link',
                  'clicked',
                  { eventId: 'sign_in_create_account_link_clicked' },
                  { to: '/register' }
                );
              }}
            />
          </div>
        )
      }
      {
        // Social sign-in methods
        signInMethods.length > 0 && enhancedSocialConnectors.length > 0 && (
          <>
            <Divider label="description.or" className={styles.divider} />
            <SocialSignInList socialConnectors={enhancedSocialConnectors} className={styles.main} />
          </>
        )
      }
    </>
  );
};

const SignIn = () => {
  const { signInMethods, socialConnectors, signInMode } = useSieMethods();
  const { agreeToTermsPolicy } = useTerms();
  const [params] = useSearchParams();
  const { t } = useTranslation();

  const [applicationName, setApplicationName] = useState<string>('');
  const { appId } = logtoCookies;

  const enhancedSocialConnectors = getEnhancedSocialConnectors(socialConnectors);

  if (!signInMode) {
    return <ErrorPage />;
  }

  if (signInMode === SignInMode.Register) {
    return <Navigate to="/register" />;
  }

  if (params.get(ExtraParamsKey.OneTimeToken)) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  useEffect(() => {
    if (!appId) {
      return;
    }

    const config = getRuntimeConfig();

    fetch(`${config.VITE_ONE_MEMBER_API_ENDPOINT}/api/applications/${appId}`)
      .then(async (res) => res.json())
      .then((data) => {
        if (data && typeof data === 'object' && 'name' in data && typeof data.name === 'string') {
          setApplicationName(data.name ?? '');
        }
      })
      .catch((error) => {
        console.error('Failed to fetch application name:', error);
      });
  }, [appId]);

  const loginHint = useLoginHint();

  return (
    <LandingPageLayout title="description.sign_in_to_your_account">
      <header className={styles.welcomeTextContainer}>
        <h1>
          {applicationName
            ? t('welcome.title_with_name', {
                // If name starts with "VIPA" followed by platform (Web, Android, iOS), display only "VIPA"
                applicationName: applicationName.replace(/^(VIPA)\s+.+$/i, '$1'),
              })
            : t('welcome.title')}
        </h1>
        <p>{t('welcome.subtitle')}</p>
      </header>
      <GoogleOneTap context="signin" />
      <SingleSignOnFormModeContextProvider>
        <Main signInMethods={signInMethods} socialConnectors={enhancedSocialConnectors} />
        <SignInFooters />
      </SingleSignOnFormModeContextProvider>
      {
        // Only show terms and privacy links for sign in page if the agree to terms policy is `Automatic` or `ManualRegistrationOnly`
        agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && (
          <TermsAndPrivacyLinks className={styles.terms} />
        )
      }
      <Footer className="sign-in-footer-mobile" />
    </LandingPageLayout>
  );
};

export default SignIn;
